<template>
  <!-- 1. Error State (clean message when player details return 500 or not available) -->
  <div
    v-if="!isLoading && isError"
    class="min-h-[60vh] flex flex-col items-center justify-center p-6 w-full"
  >
    <div
      class="bg-white rounded-3xl shadow-sm border border-slate-200/80 p-8 max-w-md w-full flex flex-col items-center text-center gap-4"
    >
      <div
        class="w-16 h-16 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center text-2xl shadow-xs border border-rose-100"
      >
        <i class="fa-solid fa-triangle-exclamation"></i>
      </div>
      <div class="flex flex-col gap-1">
        <h2 class="text-base font-bold text-slate-800 m-0">
          Player Data Unavailable
        </h2>
        <p class="text-xs text-slate-500 m-0 leading-relaxed">
          {{
            errorMessage || "Player data not available, please try again later."
          }}
        </p>
      </div>
      <div class="flex items-center gap-2 pt-2">
        <button
          @click="fetchData"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-all flex items-center gap-2 shadow-xs cursor-pointer border-none"
        >
          <i class="fa-solid fa-rotate-right text-[11px]"></i>
          <span>Try Again</span>
        </button>
        <button
          @click="goToPlayersPage"
          class="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer border border-slate-200/60"
        >
          <i class="fa-solid fa-arrow-left text-[11px]"></i>
          <span>Go to Players</span>
        </button>
      </div>
    </div>
  </div>

  <!-- 2. Main Content (only when !isLoading and !isError) -->
  <div
    class="w-full mx-auto p-3 lg:p-6 flex flex-col gap-6 text-slate-700 transition-all duration-300"
    v-else-if="!isLoading"
  >
    <!-- Top Section: Player Header & Stats + Last Transactions -->
    <div class="flex flex-col xl:flex-row items-stretch gap-6 w-full">
      <!-- Left Column: Player Info & Stats Cards -->
      <div class="flex-1 flex flex-col gap-4">
        <!-- Player Hero Card -->
        <div
          class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <!-- Avatar + Player ID + Status -->
          <div class="flex items-center gap-4 min-w-0">
            <div class="relative shrink-0">
              <img
                :src="`https://storage.googleapis.com/camasy-images/avataraugustus/${
                  player?.avatar ? 'avatar_' + player?.avatar : 'earplane'
                }.png`"
                alt="Avatar"
                class="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border-2 border-slate-100 shadow-sm object-cover bg-slate-50 p-0.5"
              />
              <span
                class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white"
                :class="
                  player?.status === 'active'
                    ? 'bg-emerald-500'
                    : player?.status === 'frozen'
                    ? 'bg-sky-500'
                    : 'bg-slate-400'
                "
              ></span>
            </div>

            <div class="flex flex-col min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <h1
                  class="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-800 tracking-tight truncate mb-0"
                >
                  {{ player.user_id || effectivePlayerId }}
                </h1>
                <!-- Status Badge -->
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize"
                  :class="statusBadgeClass"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="
                      player?.status === 'active'
                        ? 'bg-emerald-500 animate-pulse'
                        : 'bg-current'
                    "
                  ></span>
                  {{ player.status || "Unknown" }}
                </span>
                <!-- Category Switcher Select Dropdown -->
                <div class="relative inline-flex items-center">
                  <select
                    :value="effectiveCategory"
                    @change="onCategoryChange($event.target.value)"
                    class="appearance-none bg-slate-100 hover:bg-slate-200/80 text-slate-700 font-bold text-[11px] uppercase tracking-wider pl-2.5 pr-6 py-0.5 rounded-md border border-slate-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all shadow-2xs"
                    title="Switch Category"
                  >
                    <option
                      v-for="cat in availableCategories"
                      :key="cat"
                      :value="cat"
                      class="text-xs font-semibold uppercase text-slate-800 bg-white"
                    >
                      {{ formatCategoryLabel(cat) }}
                    </option>
                  </select>
                  <i
                    class="fa-solid fa-chevron-down absolute right-2 text-[8px] text-slate-400 pointer-events-none"
                  ></i>
                </div>
              </div>
              <p
                class="text-xs text-slate-400 font-medium mt-1 mb-0 flex items-center gap-2"
              >
                <span
                  >Skin:
                  <strong class="text-slate-600">{{
                    player.skin_id || effectiveSkinId || "N/A"
                  }}</strong></span
                >
                <span>•</span>
                <span
                  >Currency:
                  <strong class="text-slate-600">{{
                    player.currency || "--"
                  }}</strong></span
                >
              </p>
            </div>
          </div>

          <!-- Currency Switcher -->
          <div
            class="flex flex-col sm:items-end gap-1.5 shrink-0 self-stretch sm:self-auto justify-center"
          >
            <span
              class="text-[11px] font-bold uppercase tracking-wider text-slate-400"
              >Switch Currency</span
            >
            <div
              class="bg-slate-100 p-1 rounded-xl flex items-center gap-1 border border-slate-200/60"
            >
              <button
                v-if="Object.keys(currency).length"
                type="button"
                class="px-3 py-1.5 text-xs font-bold rounded-lg transition-all"
                :class="
                  currConv === currency.code
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                "
                @click="
                  currConv = currency.code;
                  changeCurrency(currConv);
                "
              >
                {{ currency.code }}
              </button>

              <button
                v-if="currency?.code !== 'EUR'"
                type="button"
                class="px-3 py-1.5 text-xs font-bold rounded-lg transition-all"
                :class="
                  currConv === 'EUR'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                "
                @click="
                  currConv = 'EUR';
                  changeCurrency(currConv);
                "
              >
                EUR
              </button>
            </div>
          </div>
        </div>

        <!-- Stats Container -->
        <div
          class="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden"
        >
          <!-- Stats Subtabs Header -->
          <div
            class="p-3 bg-slate-50/80 border-b border-slate-100 flex items-center gap-1.5 overflow-x-auto"
            v-if="!is_loading_summary"
          >
            <button
              class="px-3.5 py-1.5 text-xs font-bold rounded-xl transition-all whitespace-nowrap"
              :class="
                tab === 'total'
                  ? 'bg-white text-blue-600 shadow-sm border border-slate-200/60'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
              "
              @click="changeTab('total')"
            >
              Total
            </button>
          </div>

          <!-- Stats Cards Grid -->
          <div v-if="!is_loading_summary" class="p-4">
            <!-- 7 Days Card -->
            <div class="flex flex-col gap-3">
              <div class="flex items-center gap-2 mb-1">
                <div
                  class="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-sm font-semibold"
                >
                  <i class="fas fa-calendar-week"></i>
                </div>
                <h3
                  class="text-sm font-bold text-slate-800 uppercase tracking-wider mb-0"
                >
                  Last 7 Days
                </h3>
              </div>

              <div class="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                <!-- Bets Played -->
                <div
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i class="fas fa-play text-slate-400 text-[9px]"></i>
                    Bets Played
                  </span>
                  <span class="text-base font-bold text-slate-800 mt-1">
                    {{ formatNumber(summary?.last7Days?.totalBets) ?? 0 }}
                  </span>
                </div>

                <!-- Total Bets -->
                <div
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i class="fas fa-coins text-blue-500 text-[9px]"></i>
                    Total Bets
                  </span>
                  <span
                    class="text-base font-bold text-blue-600 mt-1 truncate"
                    :title="`${symbolCurr} ${formatCurrency(
                      summary?.last7Days?.totalBetAmount,
                    )}`"
                  >
                    {{ symbolCurr }}
                    {{ formatCurrency(summary?.last7Days?.totalBetAmount) }}
                  </span>
                </div>

                <!-- Total Wins -->
                <div
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i class="fas fa-trophy text-emerald-500 text-[9px]"></i>
                    Total Wins
                  </span>
                  <span
                    class="text-base font-bold text-emerald-600 mt-1 truncate"
                    :title="`${symbolCurr} ${formatCurrency(
                      summary?.last7Days?.totalWinAmount,
                    )}`"
                  >
                    {{ symbolCurr }}
                    {{ formatCurrency(summary?.last7Days?.totalWinAmount) }}
                  </span>
                </div>

                <!-- Net Profit -->
                <div
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i class="fas fa-chart-line text-amber-500 text-[9px]"></i>
                    Net Profit
                  </span>
                  <span
                    class="text-base font-bold mt-1 truncate"
                    :class="
                      getNetClass(summary?.last7Days) === 'positive'
                        ? 'text-emerald-600'
                        : getNetClass(summary?.last7Days) === 'negative'
                        ? 'text-rose-600'
                        : 'text-slate-600'
                    "
                    :title="getNetProfit(summary?.last7Days)"
                  >
                    {{ getNetProfit(summary?.last7Days) }}
                  </span>
                </div>

                <!-- Win Rate -->
                <div
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i class="fas fa-percent text-purple-500 text-[9px]"></i>
                    Win Rate
                  </span>
                  <span class="text-base font-bold text-slate-800 mt-1">
                    {{ getWinRate(summary?.last7Days) }}%
                  </span>
                </div>

                <!-- Conditional Stats -->
                <div
                  v-if="tab === 'total'"
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i class="fas fa-clock text-slate-400 text-[9px]"></i>
                    Game Time
                  </span>
                  <span class="text-base font-bold text-slate-800 mt-1">
                    {{
                      summary?.last7Days?.totalTime
                        ? formatTimeGame(summary?.last7Days?.totalTime)
                        : "00:00"
                    }}
                  </span>
                </div>

                <!-- Live Casino: Fiches Count -->
                <div
                  v-if="
                    isLiveCasino &&
                    summary?.last7Days?.fichesCount !== undefined
                  "
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i class="fas fa-circle-dot text-indigo-500 text-[9px]"></i>
                    Chips / Fiches
                  </span>
                  <span class="text-base font-bold text-slate-800 mt-1">
                    {{ formatNumber(summary?.last7Days?.fichesCount) }}
                  </span>
                </div>

                <!-- Live Casino: Max Multiplier -->
                <div
                  v-if="
                    isLiveCasino &&
                    summary?.last7Days?.maxMultiplier !== undefined
                  "
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i class="fas fa-bolt text-amber-500 text-[9px]"></i>
                    Max Multiplier
                  </span>
                  <span class="text-base font-bold text-slate-800 mt-1">
                    {{ summary?.last7Days?.maxMultiplier }}x
                  </span>
                </div>

                <div
                  v-if="tab === 'cashout'"
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i
                      class="fas fa-hand-holding-dollar text-slate-400 text-[9px]"
                    ></i>
                    Bet 1-2
                  </span>
                  <span class="text-base font-bold text-slate-800 mt-1">
                    {{ summaryCashout("last7Days") }}
                  </span>
                </div>

                <div
                  v-if="tab === 'range'"
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i class="fas fa-sliders text-slate-400 text-[9px]"></i>
                    Bet 1-2-3
                  </span>
                  <span class="text-base font-bold text-slate-800 mt-1">
                    {{ summaryRange("last7Days") }}
                  </span>
                </div>

                <div
                  v-if="tab === 'oddeven'"
                  class="bg-slate-50/80 hover:bg-slate-50 border border-slate-100 rounded-xl p-3 flex flex-col justify-between transition-colors"
                >
                  <span
                    class="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <i
                      class="fas fa-arrows-split-up-and-left text-slate-400 text-[9px]"
                    ></i>
                    Bet O-E
                  </span>
                  <span class="text-base font-bold text-slate-800 mt-1">
                    {{ summaryOddEven("last7Days") }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="w-full flex items-center justify-center p-8">
            <Loader />
          </div>
        </div>
      </div>

      <!-- Right Column: Last Transactions Widget -->
      <div
        class="w-full xl:w-[380px] shrink-0 bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 sm:p-5 flex flex-col"
        v-if="!is_loading_activity"
      >
        <div
          class="flex justify-between items-center pb-3 mb-3 border-b border-slate-100"
        >
          <div class="flex items-center gap-2">
            <div
              class="w-7 h-7 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center text-xs"
            >
              <i class="fas fa-receipt"></i>
            </div>
            <h3
              class="text-sm font-bold text-slate-800 uppercase tracking-wider mb-0"
            >
              Last Transactions
            </h3>
          </div>

          <a
            :href="viewAllTransactionsUrl"
            target="_blank"
            class="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50/80 hover:bg-indigo-100/80 px-3 py-1 rounded-full transition-all text-decoration-none"
          >
            <span>View All</span>
            <i class="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
          </a>
        </div>

        <!-- Transactions List -->
        <div
          v-if="spins && spins.length > 0"
          class="flex flex-col divide-y divide-slate-100 overflow-y-auto max-h-[380px] pr-1 custom-scroll"
        >
          <div
            v-for="spin in spins"
            :key="spin._id || spin.id || spin.created_at"
            class="py-2.5 flex items-center justify-between gap-3 hover:bg-slate-50/80 px-2 rounded-xl transition-colors group"
          >
            <div class="flex items-center gap-2.5 min-w-0">
              <span
                class="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-tight shrink-0"
                :class="getTypeBadgeClass(spin.type_id)"
              >
                {{ getType(spin.type_id) }}
              </span>

              <div class="flex flex-col min-w-0">
                <span class="text-xs font-medium text-slate-700 truncate">
                  {{ spin.game_name || spin.game_id || "Game Round" }}
                </span>
                <span class="text-[10px] text-slate-400 font-medium">
                  {{ formatDate(spin.created_at) }}
                </span>
              </div>
            </div>

            <div class="flex items-center gap-2 shrink-0">
              <span
                class="text-xs font-bold"
                :class="
                  Number(spin.win) > 0 ? 'text-emerald-600' : 'text-slate-600'
                "
              >
                {{ Number(spin.win) > 0 ? "+" : "-" }} {{ symbolCurr }}
                {{
                  (
                    Math.abs(
                      (Number(spin.amount) || 0) - (Number(spin.win) || 0),
                    ) / (Number(rateCurr) || 1)
                  ).toFixed(2)
                }}
              </span>

              <button
                v-if="roundIsNotInProgress(spin)"
                @click="showRoundDetail(spin)"
                class="w-7 h-7 rounded-lg bg-slate-100 group-hover:bg-blue-50 text-slate-500 group-hover:text-blue-600 flex items-center justify-center transition-colors border border-transparent group-hover:border-blue-200/60"
                title="View Round Details"
              >
                <i class="fa-solid fa-eye text-xs"></i>
              </button>
            </div>
          </div>
        </div>

        <div
          v-else
          class="py-12 flex flex-col items-center justify-center text-slate-400 gap-2"
        >
          <i class="fas fa-inbox text-2xl text-slate-300"></i>
          <span class="text-xs font-medium"
            >No recent activity for this player</span
          >
        </div>
      </div>
    </div>

    <!-- Main Navigation Tabs -->
    <div
      class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-1.5 flex items-center gap-1.5 overflow-x-auto w-full"
    >
      <button
        v-for="item in navTabs"
        :key="item.id"
        class="px-4 py-2 rounded-xl text-xs md:text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
        :class="
          subtab === item.id
            ? 'bg-blue-600 text-white shadow-sm'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
        "
        @click="changesubtab(item.id)"
      >
        <i :class="item.icon" class="text-xs"></i>
        <span>{{ item.label }}</span>
      </button>
    </div>

    <!-- Tab Contents -->
    <div class="w-full">
      <!-- 1. Overview Tab -->
      <div v-if="subtab === 'overview'" class="flex flex-col gap-6">
        <div
          class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4"
        >
          <div
            class="flex items-center gap-2.5 pb-4 mb-6 border-b border-slate-100"
          >
            <div
              class="w-8 h-8 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center text-sm font-bold"
            >
              <i class="fa-solid fa-star"></i>
            </div>
            <div>
              <h2 class="text-base font-bold text-slate-800 m-0">
                Personal & Connection Details
              </h2>
              <p class="text-xs text-slate-400 m-0">
                Overview of player profile, client device, and connectivity
              </p>
            </div>
          </div>

          <!-- 4 Information Section Cards Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <!-- User Identity -->
            <div
              class="bg-slate-50/70 border border-slate-100 rounded-2xl p-4 flex flex-col gap-3"
            >
              <div
                class="flex items-center gap-2 pb-2 border-b border-slate-200/60"
              >
                <i class="fa-solid fa-user text-blue-600 text-sm"></i>
                <h3
                  class="text-xs font-bold uppercase tracking-wider text-slate-700 m-0"
                >
                  User Identity
                </h3>
              </div>
              <div class="flex flex-col gap-2">
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-solid fa-fingerprint text-[10px]"></i> User
                    ID</span
                  >
                  <span class="font-bold text-slate-800">{{
                    player.user_id
                  }}</span>
                </div>
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-solid fa-coins text-[10px]"></i>
                    Currency</span
                  >
                  <span class="font-bold text-slate-800">{{
                    player?.currency ?? "--"
                  }}</span>
                </div>
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-solid fa-shield-halved text-[10px]"></i>
                    Status</span
                  >
                  <span class="font-bold capitalize text-slate-800">{{
                    player.status
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Connection Details -->
            <div
              class="bg-slate-50/70 border border-slate-100 rounded-2xl p-4 flex flex-col gap-3"
            >
              <div
                class="flex items-center gap-2 pb-2 border-b border-slate-200/60"
              >
                <i class="fa-solid fa-clock text-emerald-600 text-sm"></i>
                <h3
                  class="text-xs font-bold uppercase tracking-wider text-slate-700 m-0"
                >
                  Connection
                </h3>
              </div>
              <div class="flex flex-col gap-2">
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-solid fa-rocket text-[10px]"></i> First</span
                  >
                  <span class="font-bold text-slate-800">{{
                    formatDate(player.created_at)
                  }}</span>
                </div>
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-solid fa-clock-rotate-left text-[10px]"></i>
                    Last</span
                  >
                  <span class="font-bold text-slate-800">{{
                    formatDate(player.updated_at)
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Device & Platform -->
            <div
              class="bg-slate-50/70 border border-slate-100 rounded-2xl p-4 flex flex-col gap-3"
            >
              <div
                class="flex items-center gap-2 pb-2 border-b border-slate-200/60"
              >
                <i
                  class="fa-solid fa-mobile-screen text-purple-600 text-sm"
                ></i>
                <h3
                  class="text-xs font-bold uppercase tracking-wider text-slate-700 m-0"
                >
                  Device & Platform
                </h3>
              </div>
              <div class="flex flex-col gap-2">
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-solid fa-mobile-screen text-[10px]"></i>
                    Device</span
                  >
                  <span class="font-bold text-slate-800">{{
                    player?.client?.device ?? "N.A."
                  }}</span>
                </div>
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-solid fa-microchip text-[10px]"></i>
                    Platform</span
                  >
                  <span class="font-bold text-slate-800">{{
                    player?.client?.platform || "N.A."
                  }}</span>
                </div>
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-brands fa-chrome text-[10px]"></i>
                    Browser</span
                  >
                  <span class="font-bold text-slate-800">{{
                    player?.client?.browser ?? "N.A."
                  }}</span>
                </div>
              </div>
            </div>

            <!-- Location & Character -->
            <div
              class="bg-slate-50/70 border border-slate-100 rounded-2xl p-4 flex flex-col gap-3"
            >
              <div
                class="flex items-center gap-2 pb-2 border-b border-slate-200/60"
              >
                <i class="fa-solid fa-globe text-cyan-600 text-sm"></i>
                <h3
                  class="text-xs font-bold uppercase tracking-wider text-slate-700 m-0"
                >
                  Location & Character
                </h3>
              </div>
              <div class="flex flex-col gap-2">
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-solid fa-location-dot text-[10px]"></i> IP
                    Address</span
                  >
                  <span class="font-bold text-slate-800">{{
                    player?.client?.ip ?? "N.A."
                  }}</span>
                </div>
                <div class="flex justify-between items-center text-xs">
                  <span
                    class="text-slate-400 font-medium flex items-center gap-1.5"
                    ><i class="fa-solid fa-mask text-[10px]"></i>
                    Character</span
                  >
                  <img
                    width="28"
                    height="28"
                    :src="getCharacter(player.character)"
                    class="rounded-md object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <IpConnectionInfo v-if="ipInfo" :ipInfo="ipInfo" />
      </div>

      <div
        v-else-if="subtab === 'rounds'"
        class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 sm:p-6"
      >
        <PlayerRounds
          :player_id="player?.user_id || effectivePlayerId"
          :category="effectiveCategory"
        />
      </div>

      <div
        v-else-if="subtab === 'sessions'"
        class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 sm:p-6"
      >
        <PlayerSessions :player_id="player.user_id || effectivePlayerId" />
      </div>

      <div
        v-else-if="subtab === 'messages'"
        class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 sm:p-6"
      >
        <PlayerMessageList :user_id="player.user_id || effectivePlayerId" />
      </div>

      <div
        v-else-if="subtab === 'statistics'"
        class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 sm:p-6"
      >
        <PlayerStatistics
          :currency_code="currConv"
          :rateCurr="rateCurr"
          :user_id="player?.user_id || effectivePlayerId"
          :category="effectiveCategory"
        />
      </div>

      <div
        v-else-if="subtab === 'settings'"
        class="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 sm:p-6"
      >
        <SettingsManager :playerInfo="player" />
      </div>
    </div>
  </div>

  <div
    v-else
    class="min-h-[60vh] flex flex-col items-center justify-center gap-3 w-full"
  >
    <Loader />
    <span class="text-xs font-semibold text-slate-400 tracking-wide uppercase">
      Loading player details...
    </span>
  </div>

  <Teleport to="body">
    <!-- Live Casino Modal -->
    <LiveCasinoRoundModal
      v-if="showDetailModal && isLiveCasino"
      :roundInfo="roundInfo"
      :round_id="roundInfo?.round_id || roundInfo?.roundId || roundInfo?.uuid"
      :isVisible="showDetailModal"
      :fullInfo="true"
      :skinFilt="
        parseInt(roundInfo?.skin_id || player?.skin_id || effectiveSkinId)
      "
      :closeModal="closeModal"
      @close="closeModal"
    />
    <!-- Slot / Instant Modal -->
    <RoundModal
      v-else-if="showDetailModal"
      :title="`Round Details`"
      :closeModal="closeModal"
      :isVisible="showDetailModal"
      :roundInfo="roundInfo"
      :skinFilt="
        parseInt(roundInfo?.skin_id || player?.skin_id || effectiveSkinId)
      "
    />
  </Teleport>
</template>

<script>
import { getFetch } from "@/api/axiosClient";
import {
  fetchAppConfiguration,
  getAllowedCategories,
} from "@/helpers/appConfiguration.js";
import Loader from "./Loader.vue";
import SessionDuration from "./SessionDuration.vue";
import SettingsManager from "./PlayerDetailsComponent/SettingsManager.vue";
import PlayerMessageList from "./PlayerDetailsComponent/PlayerMessageList.vue";
import PlayerRounds from "./PlayerDetailsComponent/PlayerRounds.vue";
import IpConnectionInfo from "./PlayerDetailsComponent/IpConnectionInfo.vue";
import RoundModal from "./Modal/RoundModal.vue";
import LiveCasinoRoundModal from "@/livecasino/components/LiveCasinoRoundModal.vue";
import PlayerSessions from "./PlayerDetailsComponent/PlayerSessions.vue";
import PlayerStatistics from "./PlayerDetailsComponent/PlayerStatistics.vue";
import { mapGetters } from "vuex";

export default {
  name: "PlayerDetail",
  props: {
    playerId: {
      type: String,
      default: null,
    },
    category: {
      type: String,
      default: null,
    },
    skinId: {
      type: [String, Number],
      default: null,
    },
    initialTab: {
      type: String,
      default: "overview",
    },
  },
  data() {
    return {
      player: {},
      isError: false,
      errorMessage: "",
      selectedCategory: "",
      allowedCategories: [],
      tab: "total",
      subtab: this.initialTab || "overview",
      is_loading: true,
      is_loading_summary: true,
      is_loading_activity: true,
      summary: {},
      symbolCurr: "EUR",
      rateCurr: 1,
      spins: [],
      currency: {},
      currConv: "EUR",
      isLoading: true,
      ipInfo: null,
      showDetailModal: false,
      roundInfo: null,
      navTabs: [
        { id: "overview", label: "Overview", icon: "fa-solid fa-star" },
        { id: "rounds", label: "Rounds", icon: "fa-solid fa-play" },
        { id: "sessions", label: "Sessions", icon: "fa-solid fa-clock" },
        { id: "messages", label: "Messages", icon: "fa-solid fa-message" },
        {
          id: "statistics",
          label: "Statistics",
          icon: "fa-solid fa-chart-pie",
        },
        { id: "settings", label: "Settings", icon: "fa-solid fa-gear" },
      ],
    };
  },
  computed: {
    ...mapGetters(["multipleMultipliersGames", "skinStorage"]),
    effectivePlayerId() {
      return this.playerId || this.$route.params.id;
    },
    effectiveCategory() {
      if (this.selectedCategory) return this.selectedCategory.toLowerCase();
      if (this.category) return this.category.toLowerCase();
      if (this.$route.meta?.section)
        return this.$route.meta.section.toLowerCase();
      const path = this.$route.path.toLowerCase();
      if (path.includes("/livecasino")) return "livecasino";
      if (path.includes("/instant")) return "instant";
      return "slot";
    },
    availableCategories() {
      const baseList =
        this.allowedCategories.length > 0
          ? [...this.allowedCategories]
          : ["slot", "livecasino", "instant"];
      let list = baseList.filter((cat) => cat !== "crash");

      const current = this.effectiveCategory;
      if (current && current !== "crash" && !list.includes(current)) {
        list.push(current);
      }

      return list;
    },
    effectiveSkinId() {
      return (
        this.skinId ||
        this.player?.skin_id ||
        this.$route.params.skinFilt ||
        this.skinStorage ||
        ""
      );
    },
    isLiveCasino() {
      return this.effectiveCategory === "livecasino";
    },
    currentLang() {
      return this.$route.params.lang || localStorage.getItem("locale") || "en";
    },
    viewAllTransactionsUrl() {
      const skinPath =
        this.effectiveSkinId && this.effectiveSkinId !== "all"
          ? `/${this.effectiveSkinId}`
          : "";
      const userId = this.player.user_id || this.effectivePlayerId;

      if (this.effectiveCategory === "livecasino") {
        return `/${this.currentLang}${skinPath}/livecasino/spins?user_id=${userId}`;
      } else if (this.effectiveCategory === "instant") {
        return `/${this.currentLang}${skinPath}/instant/spins?user_id=${userId}`;
      } else {
        return `/rounds?user_id=${userId}&skin_id=${this.effectiveSkinId}`;
      }
    },
    hasOddEvenRanges() {
      const selectedDatabase =
        this.$store?.getters?.selectedDatabase || "theearcasino";
      if (this.multipleMultipliersGames?.includes(selectedDatabase)) {
        return false;
      }
      return true;
    },
    statusBadgeClass() {
      const status = (this.player?.status || "").toLowerCase();
      if (status === "active") {
        return "bg-emerald-50 text-emerald-700 border-emerald-200/80";
      } else if (status === "frozen") {
        return "bg-sky-50 text-sky-700 border-sky-200/80";
      } else if (status === "banned" || status === "blocked") {
        return "bg-rose-50 text-rose-700 border-rose-200/80";
      }
      return "bg-slate-100 text-slate-700 border-slate-200";
    },
  },
  components: {
    Loader,
    SessionDuration,
    SettingsManager,
    PlayerMessageList,
    PlayerRounds,
    IpConnectionInfo,
    RoundModal,
    LiveCasinoRoundModal,
    PlayerStatistics,
    PlayerSessions,
  },
  watch: {
    "$route.params.id"(newId) {
      if (newId && newId !== this.effectivePlayerId) {
        this.fetchData();
      }
    },
    playerId(newId) {
      if (newId) {
        this.fetchData();
      }
    },
  },
  async mounted() {
    await this.loadCategoriesConfiguration();
    this.fetchData();
  },
  methods: {
    async loadCategoriesConfiguration() {
      try {
        await fetchAppConfiguration();
        const cats = getAllowedCategories();
        if (cats && cats.length > 0) {
          this.allowedCategories = cats;
        }
      } catch (err) {
        console.error("Error loading categories configuration:", err);
      } finally {
        if (!this.selectedCategory) {
          this.selectedCategory = this.effectiveCategory;
        }
      }
    },
    formatCategoryLabel(cat) {
      if (!cat) return "";
      const map = {
        slot: "Slot",
        livecasino: "Live Casino",
        instant: "Instant",
        crash: "Crash",
      };
      return map[cat.toLowerCase()] || cat.toUpperCase();
    },
    onCategoryChange(newCategory) {
      if (!newCategory) return;
      this.selectedCategory = newCategory.toLowerCase();

      // Refresh summary and activity with the newly selected category
      this.fetchSummary();
      this.fetchActivity();

      // Update browser URL if on a category route
      const skinPath =
        this.effectiveSkinId && this.effectiveSkinId !== "all"
          ? `/${this.effectiveSkinId}`
          : "";
      const playerId = this.player?.user_id || this.effectivePlayerId;
      if (playerId && this.$route.params.lang) {
        const newPath = `/${this.currentLang}${skinPath}/${
          this.selectedCategory
        }/players/${this.player?._id || playerId}`;
        if (this.$route.path !== newPath) {
          this.$router.replace(newPath).catch(() => {});
        }
      }
    },
    goToPlayersPage() {
      const skinPath =
        this.effectiveSkinId && this.effectiveSkinId !== "all"
          ? `/${this.effectiveSkinId}`
          : "";
      const cat = this.effectiveCategory || "slot";
      const targetUrl = `/${this.currentLang}${skinPath}/${cat}/players`;
      this.$router.push(targetUrl);
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZone: "UTC",
        hour12: false,
      };
      return date.toLocaleString("it-IT", options);
    },
    getCharacter(character) {
      const gameID = this.$store?.getters?.selectedDatabase || "theearcasino";
      const basePath =
        "https://casino.theearplatform.com/theearcasino/public/earplane/images/skins/";
      let pathChar = gameID === "earplane" ? "plane_green" : "red";
      const ext = gameID === "earplane" ? ".svg" : ".png";
      if (character) pathChar = character;
      return basePath + pathChar + ext;
    },
    formatCurrency(amount) {
      if (!amount) return "0.00";
      const converted = parseFloat(amount) / parseFloat(this.rateCurr);
      return converted.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatNumber(amount) {
      if (!amount) return "0";
      return amount.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    getNetProfit(data) {
      if (
        !data ||
        (data.totalBetAmount === undefined && data.net === undefined)
      ) {
        return `${this.symbolCurr} 0.00`;
      }
      let net;
      if (data.net !== undefined) {
        net = parseFloat(data.net) / parseFloat(this.rateCurr || 1);
      } else {
        const wins =
          parseFloat(data.totalWinAmount || 0) / parseFloat(this.rateCurr || 1);
        const bets =
          parseFloat(data.totalBetAmount || 0) / parseFloat(this.rateCurr || 1);
        net = bets - wins;
      }
      const sign = net > 0 ? "+" : net < 0 ? "-" : "";
      const formattedNet = Math.abs(net).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${sign}${this.symbolCurr} ${formattedNet}`;
    },
    getNetClass(data) {
      if (
        !data ||
        (data.totalBetAmount === undefined && data.net === undefined)
      )
        return "neutral";
      const net =
        data.net !== undefined
          ? parseFloat(data.net)
          : parseFloat(data.totalBetAmount || 0) -
            parseFloat(data.totalWinAmount || 0);
      return net > 0 ? "positive" : net < 0 ? "negative" : "neutral";
    },
    getWinRate(data) {
      if (!data || !data.totalBets || !data.totalWins) return "0.00";
      const betsPlayed = data.totalBetAmount;
      const betsWon = data.totalWinAmount;
      return ((betsWon / betsPlayed) * 100).toFixed(2);
    },
    formatTimeGame(time) {
      const totalMinutes = Math.floor(time / 60000);
      const hours = Math.floor(totalMinutes / 60)
        .toString()
        .padStart(2, "0");
      const minutes = (totalMinutes % 60).toString().padStart(2, "0");
      return `${hours}h:${minutes}m`;
    },
    summaryOddEven(type) {
      const summary =
        type === "allTime" ? this.summary.allTime : this.summary.last7Days;
      if (!summary?.totalBets) return "-";
      const odd = parseFloat(
        ((summary.totalOdd || 0) / summary.totalBets) * 100,
      ).toFixed(2);
      const even = parseFloat(100 - odd).toFixed(2);
      return `${odd}% - ${even}%`;
    },
    getType(type_id) {
      const selectedDatabase =
        localStorage.getItem("selectedDatabase") ?? "augustus";
      if (!this.multipleMultipliersGames?.includes(selectedDatabase)) {
        return type_id === 1
          ? "Cashout 1"
          : type_id === 2
          ? "Cashout 2"
          : type_id === 3
          ? "Even/Odd"
          : type_id === 4
          ? "Ranges"
          : type_id === "spin"
          ? "Spin"
          : "Bet";
      } else {
        return type_id <= 3 ? `Cashout ${type_id}` : "Winner Race";
      }
    },
    getTypeBadgeClass(type_id) {
      if (type_id === 1 || type_id === "spin") {
        return "bg-blue-50 text-blue-700 border border-blue-200/60";
      } else if (type_id === 2) {
        return "bg-purple-50 text-purple-700 border border-purple-200/60";
      } else if (type_id === 3) {
        return "bg-amber-50 text-amber-700 border border-amber-200/60";
      }
      return "bg-slate-100 text-slate-700 border border-slate-200/60";
    },
    summaryCashout(type) {
      const summary =
        type === "allTime" ? this.summary.allTime : this.summary.last7Days;
      if (!summary?.totalBets) return "-";
      const one = parseFloat(
        ((summary.totalOne || 0) / summary.totalBets) * 100,
      ).toFixed(2);
      const two = parseFloat(100 - one).toFixed(2);
      return `${one}% - ${two}%`;
    },
    summaryRange(type) {
      const summary =
        type === "allTime" ? this.summary.allTime : this.summary.last7Days;
      if (!summary?.totalBets) return "-";
      const zero = parseFloat(
        ((summary.totalZero || 0) / summary.totalBets) * 100,
      ).toFixed(2);
      const one = parseFloat(
        ((summary.totalOne || 0) / summary.totalBets) * 100,
      ).toFixed(2);
      const two = parseFloat(100 - zero - one).toFixed(2);
      return `${zero}% - ${one}% - ${two}%`;
    },
    changeTab(tab) {
      this.tab = tab;
      this.fetchSummary();
    },
    changeCurrency(curr) {
      if (curr === "EUR") {
        this.symbolCurr = "€";
        this.rateCurr = this.currency.rate || 1;
      } else {
        this.symbolCurr = this.currency.code || this.player.currency || "EUR";
        this.rateCurr = 1;
      }
    },
    changesubtab(tab) {
      this.subtab = tab;
    },
    async fetchData() {
      if (!this.effectivePlayerId) {
        this.isError = true;
        this.errorMessage = "Player ID not specified.";
        this.isLoading = false;
        return;
      }
      this.isLoading = true;
      this.isError = false;
      this.errorMessage = "";
      try {
        const response = await getFetch(`players/${this.effectivePlayerId}`);
        if (
          !response ||
          !response.player ||
          Object.keys(response.player).length === 0
        ) {
          throw new Error("Player data not available");
        }
        this.player = response.player ?? {};
        this.currency = response.currency ? { ...response.currency } : {};

        this.rateCurr = 1;
        this.symbolCurr = this.currency.symbol || "€";

        if (this.currency?.code) {
          this.currConv = this.currency.code;
        } else {
          this.changeCurrency("EUR");
        }

        this.fetchSummary();
        this.fetchActivity();

        if (this.player?.client?.ip) {
          this.getIpInfo(this.player?.client?.ip);
        }
      } catch (error) {
        console.error("Error loading player details:", error);
        this.isError = true;
        this.errorMessage =
          "Player data not available, please try again later.";
      } finally {
        this.isLoading = false;
      }
    },
    async getIpInfo(ip) {
      try {
        const res = await fetch(`https://ipwhois.app/json/${ip}`);
        const data = await res.json();
        if (data.success) {
          this.ipInfo = data;
        }
      } catch (err) {
        console.error("Error fetching IP info:", err);
      }
    },
    /**
     * Dispatcher per il recupero del summary statistico in base alla categoria attiva
     */
    async fetchSummary() {
      if (!this.player.user_id && !this.effectivePlayerId) return;
      this.is_loading_summary = true;
      try {
        const userId = this.player.user_id || this.effectivePlayerId;
        const category = (this.effectiveCategory || "slot").toLowerCase();

        switch (category) {
          case "livecasino":
            await this.fetchLiveCasinoSummary(userId);
            break;
          case "instant":
            await this.fetchInstantSummary(userId);
            break;
          case "slot":
          default:
            await this.fetchSlotSummary(userId);
            break;
        }
      } catch (error) {
        console.error("Error fetching summary:", error);
      } finally {
        this.is_loading_summary = false;
      }
    },

    /**
     * Recupero dati 7 giorni per categoria LIVE CASINO (Roulette)
     * Endpoint: /players/statistics-summary-roulette
     */
    async fetchLiveCasinoSummary(userId) {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const formatDayStart = (d) =>
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

      const today = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
      );
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const startDate = formatDayStart(sevenDaysAgo);
      const endDate = formatDayStart(today);

      const response = await getFetch(
        `players/statistics-summary-roulette?user_id=${userId}&start_date=${startDate}&end_date=${endDate}`,
      );

      this.summary = this.normalizeLiveCasinoSummary(response);
    },

    /**
     * Normalizzatore per i dati statistici di Live Casino
     */
    normalizeLiveCasinoSummary(response) {
      if (!response) return {};
      const total = response.total || {};
      const sessions = response.sessions || {};

      return {
        ...response,
        last7Days: {
          totalBets: total.totalBets || 0,
          totalWins: total.totalWins || 0,
          totalBetAmount: total.totalBetAmount || 0,
          totalWinAmount: total.totalWinAmount || 0,
          totalTime: sessions.playTime || 0,
          net: total.net,
          fichesCount: total.fichesCount || 0,
          maxMultiplier: total.maxMultiplier || 0,
        },
      };
    },

    /**
     * Recupero dati summary per categoria SLOT
     */
    async fetchSlotSummary(userId) {
      const response = await getFetch(
        `players/summary/${userId}?type=${this.tab}`,
      );
      this.summary = this.normalizeSlotSummary(response);
    },

    /**
     * Normalizzatore per i dati statistici di Slot
     */
    normalizeSlotSummary(response) {
      return response || {};
    },

    /**
     * Recupero dati summary per categoria INSTANT
     */
    async fetchInstantSummary(userId) {
      const response = await getFetch(
        `players/summary/${userId}?type=${this.tab}`,
      );
      this.summary = this.normalizeInstantSummary(response);
    },

    /**
     * Normalizzatore per i dati statistici di Instant
     */
    normalizeInstantSummary(response) {
      return response || {};
    },
    async fetchActivity() {
      if (!this.player.user_id && !this.effectivePlayerId) return;
      this.is_loading_activity = true;
      try {
        const userId = this.player.user_id || this.effectivePlayerId;
        const skinId = this.player.skin_id || this.effectiveSkinId;
        const response = await getFetch(
          `players/lastspin/${userId}?skin_id=${skinId}`,
        );
        this.spins = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error("Error fetching activity:", error);
        this.spins = [];
      } finally {
        this.is_loading_activity = false;
      }
    },
    showRoundDetail(spin) {
      this.roundInfo = spin;
      this.showDetailModal = true;
    },
    closeModal() {
      this.showDetailModal = false;
    },
    roundIsNotInProgress(spin) {
      if (!spin?.created_at) return true;
      const createdAt = new Date(spin.created_at);
      const now = new Date();
      const diffMs = now - createdAt;
      return diffMs >= 5 * 60 * 1000;
    },
  },
};
</script>

<style scoped>
.custom-scroll::-webkit-scrollbar {
  width: 5px;
}
.custom-scroll::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 9999px;
}
.custom-scroll::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
